import { Stack, useGlobalSearchParams, usePathname, useSegments } from 'expo-router';
import { useEffect, useState } from 'react';

import { AppAbilityProvider } from '@/components/AppAbilityProvider';
import { GateRedirect } from '@/components/GateRedirect';
import { HeaderSlot, HeaderSlotProvider } from '@/components/headerSlot';
import { NavBar } from '@/components/navBar';
import { hidesOpenNavBar, ROUTES } from '@/constants/routes';
import { BASE_STACK_SCREEN_OPTIONS } from '@/constants/screenOptions';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useColors } from '@/hooks/useColors';
import { useRedirectPath } from '@/hooks/useRedirectPath';
import { clearStaleUserIdentity } from '@/services/session';
import { resolveOpenGroupDestination } from '@/util/openGroupDestination';
import { buildRouteQueryString } from '@/util/routeQuery';
import { isEHRSession } from '@/util/smartLaunch';

/**
 * Open layout — the app's public face, and the entry point for the auth flow.
 *
 * Everything here renders without an account: the landing page, the public directory,
 * community and content-library browsing, share links, expert links, and the screens a
 * visitor signs in or signs up through. A signed-in reader who arrives on one of these is
 * forwarded into the secure app, unless the route is one the route constants name as an
 * exception.
 *
 * Two things this layout deliberately does differently from its secure counterpart:
 *
 * - **It never renders nothing.** The secure layout holds its stack back until the session
 *   has resolved, which is right for routes that have nothing to show a stranger. Every
 *   anonymous visitor passes through here on every cold load, so withholding the navigator
 *   while a session restores would blank the public directory and the share links for the
 *   readers those pages exist for. The decision waits; the navigator does not.
 *
 * - **It decides from the live pathname.** The secure layout reads a captured destination,
 *   because rendering a redirect into a different top-level group tears its own stack down
 *   and collapses `usePathname()` mid-transition. Measured from inside this group, the
 *   pathname and segments stay intact across the forward into the secure group, so there is
 *   nothing here to capture around. Re-measure before assuming that still holds.
 */
export default function OpenLayout() {
  const { colors, statusBarStyle } = useColors();

  const pathname = usePathname();

  const segments = useSegments();

  const searchParams = useGlobalSearchParams();

  const { redirectPath } = useRedirectPath();

  const { userId, isLoading, isAuthResolved, needsNpiVerification } = useAuthStatus();

  /*
   * One render after mount, for a reason that has nothing to do with routing.
   *
   * A Gluestack button eats its first press when the press itself is the first thing to
   * re-render the button's subtree: the label is a text node, and re-rendering one while the
   * button is handling a press swaps the underlying element, so the press lands on one node
   * and the release on another and the browser never reports a click. Measured in a release
   * build; development never shows it, because React renders every subtree twice there. A
   * button whose subtree has re-rendered even once beforehand is unaffected, which is the
   * whole of what this does.
   *
   * The public nav bar sits in this layout and its sign-in and sign-up buttons are the ones
   * readers meet first, so until the button is fixed something here has to do the warming.
   * This layout used to do it by accident, through an account-linking lookup it no longer
   * needs. Delete this the moment the button stops remounting its label.
   */
  const [, warmTheNavBarButtons] = useState(false);

  useEffect(() => {
    warmTheNavBarButtons(true);
  }, []);

  useEffect(() => {
    if (!userId) {
      void clearStaleUserIdentity();
    }
  }, [userId]);

  const destination = resolveOpenGroupDestination({
    pathname,
    query: buildRouteQueryString(searchParams, segments),
    isAuthResolved,
    userId: userId ?? null,
    isUserLoading: isLoading,
    needsNpiVerification,
    redirectPath,
  });

  if (destination) {
    return <GateRedirect href={destination} />;
  }

  /*
   * An EHR tab whose session ended would otherwise fall through to the public pages, whose
   * Log in leads to a Google sign-in the EHR's frame refuses. The SMART routes are exempt,
   * since they are where such a tab starts over.
   */
  if (isAuthResolved && !userId && isEHRSession() && !pathname.startsWith('/auth/smart')) {
    return <GateRedirect href={ROUTES.SMART_ENDED} />;
  }

  return (
    <AppAbilityProvider userId={userId ?? undefined}>
      <HeaderSlotProvider>
        {hidesOpenNavBar(pathname) ? null : <NavBar />}

        <HeaderSlot />

        {/*
         * Every route in this group is declared, in one reviewable list, with `index` first.
         *
         * Routes come from the filesystem whether or not they appear here, so a declaration
         * changes nothing on its own — it is what a guard attaches to, and a screen missing
         * from this list is a screen no rule here can reach. Declaration order decides where
         * the navigator opens, because nothing sets `initialRouteName`, and before this list
         * existed the only declared screen was the full-screen survey modal. Keep `index`
         * first.
         */}
        <Stack screenOptions={{ ...BASE_STACK_SCREEN_OPTIONS, statusBarStyle }}>
          <Stack.Screen name="index" />

          <Stack.Screen name="ask/[slug]" />

          <Stack.Screen name="auth/callback" />

          <Stack.Screen name="auth/magic-link" />

          <Stack.Screen name="auth/smart/index" />

          <Stack.Screen name="auth/smart/launch" />

          <Stack.Screen name="auth/smart/error" />

          <Stack.Screen name="auth/smart/ended" />

          <Stack.Screen name="community/[id]" />

          <Stack.Screen name="community/shared/[token]" />

          <Stack.Screen name="consent/[token]" />

          <Stack.Screen name="content-library/[id]" />

          <Stack.Screen name="content-library/shared/[token]" />

          <Stack.Screen name="create-account/index" />

          <Stack.Screen name="create-account/step-1" />

          <Stack.Screen name="create-account/step-2" />

          <Stack.Screen name="create-password/index" />

          <Stack.Screen name="create-password/success" />

          <Stack.Screen name="create-password/verification-code-expired" />

          <Stack.Screen name="(sections)" />

          <Stack.Screen name="directory/inquiry/[id]" />

          <Stack.Screen name="email-link/index" />

          <Stack.Screen name="email-link/success" />

          <Stack.Screen name="forgot-password/index" />

          <Stack.Screen name="forgot-password/success" />

          <Stack.Screen name="inquiry" />

          <Stack.Screen name="login" />

          <Stack.Screen name="logout" />

          <Stack.Screen name="medical-on-demand/inquiries/[teamId]" />

          <Stack.Screen name="new-password" />

          <Stack.Screen name="profile/[id]" />

          <Stack.Screen name="redirect" />

          <Stack.Screen
            name="survey/[id]"
            options={{
              ...BASE_STACK_SCREEN_OPTIONS,
              presentation: 'fullScreenModal',
              gestureEnabled: false,
              animation: 'slide_from_bottom',
              statusBarBackgroundColor: colors.background[0],
              statusBarTranslucent: true,
            }}
          />
        </Stack>
      </HeaderSlotProvider>
    </AppAbilityProvider>
  );
}
